/**
 * Native generation input v1. The old wizard-schemas describe unversioned
 * native forms, not the web generation request. Keep those schemas intact so
 * saved answers can be recognised and reviewed before any conversion.
 *
 * This is deliberately a small supported subset of the web request. Add a
 * field only after checking its actual render path in the web generator.
 * generationDraftVersion identifies saved form shape only. Durable native
 * request/attempt identity belongs to a separate protocol and is not active
 * in the current draft-only mobile endpoint.
 */
import { z } from "zod";
import { RamsRiskBaselinePreconditionV1Schema } from "../rams/risk-baselines";
import { RamsHazardAssessmentsSchema } from "../rams/hazard-assessments";
import { RamsHazardEditsSchema } from "../rams/hazard-edits";
import { INT32_MAX, countSchema, entityIdSchema, generationContractorCountSchema } from "../common";
import { ProjectExpectedValuesSchema, type ProjectExpectedValues } from "../entities/project";
import { GenerationProjectInputReviewStrictSchema } from "../generation-project-review";

export const GENERATION_DRAFT_VERSION = 1 as const;
// Current native clients opt into additive v1 draft fields on GET. Installed
// strict-v1 readers omit this query capability and receive a legacy projection.
export const GENERATION_DRAFT_EXTENDED_SHAPE = "extended-v1" as const;
// Full CPP's client answer and project-edit intent are additive to extended-v1.
// Installed strict readers must request only the shape they understand.
export const GENERATION_DRAFT_CPP_INTENT_SHAPE = "cpp-intent-v2" as const;
export const GENERATION_DRAFT_PROJECT_BASELINE_SHAPE = "project-baseline-v3" as const;
// SI/EP and both CPP editors negotiate their source version separately so older
// strict draft readers never receive a field they cannot parse.
export const GENERATION_DRAFT_DOCUMENT_BASIS_SHAPE = "document-basis-v4" as const;
export const GENERATION_DRAFT_RAMS_ASSESSMENTS_SHAPE = "rams-assessments-v5" as const;
export const GENERATION_DRAFT_CPP_CONTACTS_SHAPE = "cpp-contacts-v7" as const;
export const CPP_CONTACT_FIELDS = ["principalContractorCompany", "principalContractorPhone", "principalContractorEmail", "architectPhone", "architectEmail", "structuralEngineerCompany", "structuralEngineerPhone", "structuralEngineerEmail"] as const;
export const GENERATION_DRAFT_RAMS_HAZARD_EDITS_SHAPE = "rams-hazard-edits-v6" as const;
/** Request-only PATCH attestation for explicit SI/EP/CPP source or CPP variant review. */
export const ReviewedBasisVersionIdSchema = entityIdSchema.nullable();
export type ReviewedBasisVersionId = z.infer<typeof ReviewedBasisVersionIdSchema>;

const nullableText = z.string().nullable().optional();

// The fields the native CPP editor collects (its draft answers below).
const cppNativeProjectFieldsTouchedFields = [
  "clientName", "siteAccessArrangements", "deliveryArrangements", "principalContractor", "siteHoarding",
  "safetyArrangements", "trainingRequirements", "architectName", "architectCompany",
  "structuralEngineer", "principalDesigner", "clientContactName", "clientContactPhone",
  "clientContactEmail", "clientAddress", "principalDesignerPhone", "principalDesignerEmail",
  "principalDesignerCompany",
  ...CPP_CONTACT_FIELDS,
] as const;
const cppFullProjectFieldsTouchedFields = [
  ...cppNativeProjectFieldsTouchedFields,
] as const;
const cppLightweightProjectFieldsTouchedFields = [
  "lightweightSiteRisks", "lightweightWelfare", "lightweightSiteRules",
] as const;
export const CPP_PROJECT_GENERATION_FIELDS = [
  ...cppFullProjectFieldsTouchedFields, ...cppLightweightProjectFieldsTouchedFields,
] as const;
export type CppProjectFieldTouched = (typeof CPP_PROJECT_GENERATION_FIELDS)[number];

const cppProjectFieldsTouched = z.array(z.enum(CPP_PROJECT_GENERATION_FIELDS));
const cppNativeProjectFieldsTouched = z.array(z.enum(cppNativeProjectFieldsTouchedFields));
const cppLightweightProjectFieldsTouched = z.array(z.enum(cppLightweightProjectFieldsTouchedFields));

export const AuthoritativeProjectFactsV1Schema = z.object({
  title: z.string().min(1),
  siteAddress: z.string().min(1),
  scopeDescription: nullableText,
  domesticClient: z.boolean().nullable().optional(),
  estimatedDuration: nullableText,
  contractorCount: generationContractorCountSchema,
}).strict();
export type AuthoritativeProjectFactsV1 = z.infer<typeof AuthoritativeProjectFactsV1Schema>;

const draftHeader = { generationDraftVersion: z.literal(GENERATION_DRAFT_VERSION) };

export const RamsCanonicalGenerationDraftV1Schema = z.object({
    ramsRiskBaselinePrecondition:RamsRiskBaselinePreconditionV1Schema.optional(),
    ...draftHeader,
    documentType: z.literal("RAMS"),
    // Exact singleton source opened by this assessment editor; never infer it
    // from a later prefill refresh or the draft's save time.
    basisVersionId: entityIdSchema.nullable().optional(),
    // Original Project values; never relabel authored answers on background refresh.
    projectExpectedValues: ProjectExpectedValuesSchema.optional(),
    // Set only on the frozen generation request after deliberate input review.
    projectInputReview: GenerationProjectInputReviewStrictSchema.optional(),
    // Hazards: exact library slugs plus `custom-<id>` account-custom
    // pseudo-slugs — both resolved tenant-scoped server-side (a slug that
    // stopped being active simply drops out of the register). Project custom
    // hazards are auto-included server-side and are deliberately NOT
    // client-selectable. Personnel mirrors the web wizard's selectedWorkers
    // shape, but only each worker's `id` is honoured: the server re-resolves
    // every id against the project's CURRENT worker rows and prints their
    // name/role/company/First Aid/Fire Warden, dropping ids no longer on the
    // project (lib/rams-context.ts resolveRamsPersonnel). The other fields
    // are a display cache of what the user picked, never printed.
    // supervisorName is the works supervisor the user typed for this RAMS —
    // null until they change the field the editor seeds from the project.
    // The server resolves ONE supervisor from it (null/blank = the project's)
    // for the cover row, the personnel star and "Approved by" (after the
    // #2595 printed default approver), and writes a changed name back to the
    // project in the version's own transaction, as the web wizard's PATCH
    // does. So the payload carries no approvedBy of its own for RAMS.
    // Sequence-of-works tasks are NOT in the
    // draft: they are project state (library + custom trade tasks) the server
    // reads itself, and a RAMS refuses to generate with zero tasks.
    answers: z.object({
      selectedHazardSlugs: z.array(z.string().trim().min(1)),
      hazardAssessments: RamsHazardAssessmentsSchema.optional(),
      hazardEdits: RamsHazardEditsSchema.optional(),
      selectedWorkers: z
        .array(
          z.object({
            id: z.string().min(1),
            name: z.string().min(1),
            company: z.string().nullable().optional(),
            role: z.string().min(1),
            phone: z.string().nullable().optional(),
            firstAidTrained: z.boolean(),
            fireWardenTrained: z.boolean().optional(),
          }).strict(),
        ),
      supervisorName: nullableText,
    }).strict(),
  }).strict();

export const CanonicalGenerationDraftV1Schema = z.discriminatedUnion("documentType", [
  z.object({
    ...draftHeader,
    documentType: z.literal("COSHH"),
    // null explicitly requests server/default printed-approver resolution.
    // A manual name is retained when no governing default overrides it.
    answers: z.object({
      preparedBy: nullableText,
      approvedBy: z.string().trim().min(1).nullable(),
    }).strict(),
  }).strict(),
  z.object({
    ...draftHeader,
    documentType: z.literal("SITE_INDUCTION"),
    // Null means this editor opened before any current version existed.
    // Absent is legacy input, never authority to replace an existing version.
    basisVersionId: entityIdSchema.nullable().optional(),
    answers: z.object({ reportingProcedures: nullableText }).strict(),
  }).strict(),
  z.object({
    ...draftHeader,
    documentType: z.literal("EMERGENCY_PLAN"),
    basisVersionId: entityIdSchema.nullable().optional(),
    // Site safety details remain current Project facts. Emergency contacts
    // are an editable document field in the web wizard, with a direct render
    // destination; old native contact strings still need explicit review.
    answers: z.object({ emergencyContacts: nullableText }).strict(),
    // Only the parity editor can attest that a null was deliberately authored.
    // An unmarked null from an installed older build is an untouched blank.
    emergencyContactsAuthoredEmpty: z.literal(true).optional(),
  }).strict(),
  z.object({
    ...draftHeader,
    documentType: z.literal("CPP_LIGHTWEIGHT"),
    // Actual shared CPP singleton version opened by this editor. Independent
    // of whether that version contains full-CPP document-only answers.
    basisVersionId: entityIdSchema.nullable().optional(),
    // Present opts into current-Project resolution; [] means the native
    // editor authored no project facts. Absent remains legacy-client behavior.
    cppProjectFieldsTouched: cppLightweightProjectFieldsTouched.optional(),
    // Old inline risk/welfare answers can write back to Project facts in the
    // current web route.
    answers: z.object({ preparedBy: nullableText }).strict(),
  }).strict(),
  RamsCanonicalGenerationDraftV1Schema,
  z.object({
    ...draftHeader,
    documentType: z.literal("CPP"),
    // Original Project values; never relabel authored answers on background refresh.
    projectExpectedValues: ProjectExpectedValuesSchema.optional(),
    // Set only on the frozen generation request after deliberate input review.
    projectInputReview: GenerationProjectInputReviewStrictSchema.optional(),
    // Only direct editor changes in this session are listed. Hydrated project
    // values and answers restored from drafts/versions are not intent. This
    // includes clientName, which is a Project-backed fact.
    cppProjectFieldsTouched: cppNativeProjectFieldsTouched.optional(),
    // The actual shared CPP singleton pointer when this editor opened,
    // including a lightweight current CPP. Null means no CPP existed; absent
    // belongs only to installed clients without a source stamp. Full-answer
    // restoration remains separate in cppCurrentVersion prefill.
    basisVersionId: entityIdSchema.nullable().optional(),
    // Full CPP: exactly the fields the web wizard collects
    // (components/documents/cpp-steps) - site management/access, risk & safety
    // and welfare/emergency text fields. The core persists them to the Project
    // row (cppFieldNames write-back) exactly as the web does, so the user is
    // authoring project facts through the same reviewed path. Site rules,
    // key risks, welfare and emergency content are deliberately absent: the
    // web wizard stopped collecting them (single source = project settings).
    // Site photos are a separate surface (#2340 family) - imageIds is not
    // part of this draft: the native generation adapter carries the web
    // wizard's selection itself (lib/documents/cpp-regeneration-prefill.ts).
    answers: z.object({
      // Web collects this in the wizard (seeded from the signed-in user's
      // name) and the template prints it in the sign-off block.
      preparedBy: nullableText,
      // A typed approver only (#2821 follow-up review). null asks the server
      // to resolve the printed default approver (#2595), which also wins over
      // a typed name; the editor never echoes that displayed default back.
      approvedBy: nullableText,
      clientName: nullableText,
      principalContractor: nullableText,
      principalContractorCompany: nullableText,
      principalContractorPhone: nullableText,
      principalContractorEmail: nullableText,
      architectPhone: nullableText,
      architectEmail: nullableText,
      structuralEngineerCompany: nullableText,
      structuralEngineerPhone: nullableText,
      structuralEngineerEmail: nullableText,

      principalDesigner: nullableText,
      principalDesignerCompany: nullableText,
      principalDesignerPhone: nullableText,
      principalDesignerEmail: nullableText,
      architectName: nullableText,
      architectCompany: nullableText,
      structuralEngineer: nullableText,
      clientContactName: nullableText,
      clientContactPhone: nullableText,
      clientContactEmail: nullableText,
      clientAddress: nullableText,
      startDate: nullableText,
      siteAccessArrangements: nullableText,
      deliveryArrangements: nullableText,
      publicProtection: nullableText,
      siteHoarding: nullableText,
      safetyArrangements: nullableText,
      trainingRequirements: nullableText,
      asbestosManagement: nullableText,
      permitToWork: nullableText,
      environmentalManagement: nullableText,
    }).strict(),
  }).strict(),
]).superRefine((draft, ctx) => {
  if ((draft.documentType === "CPP" || draft.documentType === "RAMS") &&
      draft.projectInputReview !== undefined && draft.projectExpectedValues === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["projectExpectedValues"],
      message: "A relational input review stamp requires the project field baseline." });
  }
});
export type CanonicalGenerationDraftV1 = z.infer<typeof CanonicalGenerationDraftV1Schema>;

/**
 * Full-CPP answers the generator prints ONLY from the request. No Project
 * column backs them (startDate, preparedBy and a typed approvedBy print from
 * the request, not the Project row), so a regeneration keeps each one only
 * when the editor carries it forward from the current version's stored
 * payload, as the website wizard's prefill does. Every other full-CPP answer
 * is written back to the Project and prints from there when left blank
 * (#2821 follow-up). approvedBy carries only a name someone typed: a printed
 * default approver's (#2595) is re-resolved at generation, never carried.
 */
export const CPP_DOCUMENT_ONLY_FIELDS = [
  "preparedBy", "approvedBy", "startDate", "publicProtection",
  "asbestosManagement", "permitToWork", "environmentalManagement",
] as const;
export type CppDocumentOnlyField = (typeof CPP_DOCUMENT_ONLY_FIELDS)[number];

const cppDocumentOnlyAnswers = {
  preparedBy: z.string().nullable(),
  approvedBy: z.string().nullable(),
  startDate: z.string().nullable(),
  publicProtection: z.string().nullable(),
  asbestosManagement: z.string().nullable(),
  permitToWork: z.string().nullable(),
  environmentalManagement: z.string().nullable(),
} satisfies Record<CppDocumentOnlyField, z.ZodTypeAny>;

/**
 * What the CURRENT full-CPP version printed for those answers, read by the
 * native editor before a regeneration. A field in `unrecorded` was never
 * stored with that version (plans made before these answers were kept): it
 * is UNKNOWN, not blank, and the editor must ask for it rather than silently
 * drop the section it printed. Its `answers` value is then null.
 *
 * `createdAt` stamps the version: a saved editor draft older than it predates
 * the version and is not resumed over it without asking.
 */
export const CppCurrentVersionPrefillV1Schema = z.object({
  documentId: entityIdSchema,
  versionId: entityIdSchema,
  versionNumber: z.number().int().min(1).max(INT32_MAX),
  createdAt: z.string().datetime(),
  answers: z.object(cppDocumentOnlyAnswers),
  // Open on the wire (#2411): a field a newer server starts keeping must not
  // fail an installed editor closed. The editor ignores names it does not
  // know; the server boundary (Strict below) emits only known fields.
  unrecorded: z.array(z.string()),
});
export type CppCurrentVersionPrefillV1 = z.infer<typeof CppCurrentVersionPrefillV1Schema>;

/**
 * The printed "Approved by" a FULL CPP generation resolves (#2595): the
 * account's default approver's name, shown read-only and never sent back as
 * typed input, or null when the rule does not apply; `warning` names an
 * approver with no name on their profile.
 */
const cppPrintedApproverFields = {
  name: z.string().nullable(),
  warning: z.string().nullable(),
};

/**
 * The full-CPP generation preflight's prefill fields. REQUIRED on the wire so
 * an older server fails the native editor closed rather than opening it blank
 * over a version it could not read. `cppCurrentVersion` null means no
 * full-CPP version exists; `cppPhotoCount` is how many site photos a native
 * regeneration will include (the current version's selection plus every
 * photo flagged for the CPP since - the web wizard's merge).
 */
export const CppRegenerationPrefillResponseV1Schema = z.object({
  // Actual shared CPP singleton pointer for both variants. Unlike
  // cppCurrentVersion, this remains populated for a lightweight current CPP.
  cppSourceCurrentVersionId: entityIdSchema.nullable(),
  cppCurrentVersion: CppCurrentVersionPrefillV1Schema.nullable(),
  cppPhotoCount: countSchema,
  cppPrintedApprover: z.object(cppPrintedApproverFields),
});
export type CppRegenerationPrefillResponseV1 = z.infer<typeof CppRegenerationPrefillResponseV1Schema>;

/** Server-boundary variant (#2411): the wire base stays permissive for installed builds. */
export const CppCurrentVersionPrefillV1Strict = CppCurrentVersionPrefillV1Schema.extend({
  answers: z.object(cppDocumentOnlyAnswers).strict(),
  unrecorded: z.array(z.enum(CPP_DOCUMENT_ONLY_FIELDS)),
}).strict();

/** Server-boundary variant of the whole prefill (#2411). */
export const CppRegenerationPrefillResponseV1Strict = z.object({
  cppSourceCurrentVersionId: entityIdSchema.nullable(),
  cppCurrentVersion: CppCurrentVersionPrefillV1Strict.nullable(),
  cppPhotoCount: countSchema,
  cppPrintedApprover: z.object(cppPrintedApproverFields).strict(),
}).strict();

/** Strict native subset; the actual web validator is asserted in root tests. */
export const CanonicalGenerationPayloadSubsetV1Schema = z.object({
  expectedValues: ProjectExpectedValuesSchema.optional(),
  confirmedProjectData: AuthoritativeProjectFactsV1Schema,
  selectedHazardSlugs: z.array(z.string()),
  preparedBy: nullableText,
  approvedBy: nullableText,
  reportingProcedures: nullableText,
  emergencyContacts: nullableText,
  lightweight: z.boolean().optional(),
  // Present means the caller opts into current-Project resolution and only
  // listed nonblank fields may write back. Empty is distinct from absent.
  cppProjectFieldsTouched: cppProjectFieldsTouched.optional(),
  // CPP-only (web validator asserts this subset against the real request).
  clientName: z.string().nullable().optional(),
  principalDesigner: z.string().nullable().optional(),
  principalContractor: z.string().nullable().optional(),
  architectName: z.string().nullable().optional(),
  architectCompany: z.string().nullable().optional(),
  structuralEngineer: z.string().nullable().optional(),
  clientContactName: z.string().nullable().optional(),
  clientContactPhone: z.string().nullable().optional(),
  clientContactEmail: z.string().nullable().optional(),
  clientAddress: z.string().nullable().optional(),
  principalDesignerPhone: z.string().nullable().optional(),
  principalDesignerEmail: z.string().nullable().optional(),
  principalDesignerCompany: z.string().nullable().optional(),
  principalContractorCompany: z.string().nullable().optional(),
  principalContractorPhone: z.string().nullable().optional(),
  principalContractorEmail: z.string().nullable().optional(),
  architectPhone: z.string().nullable().optional(),
  architectEmail: z.string().nullable().optional(),
  structuralEngineerCompany: z.string().nullable().optional(),
  structuralEngineerPhone: z.string().nullable().optional(),
  structuralEngineerEmail: z.string().nullable().optional(),
  startDate: z.string().nullable().optional(),
  siteAccessArrangements: z.string().nullable().optional(),
  deliveryArrangements: z.string().nullable().optional(),
  publicProtection: z.string().nullable().optional(),
  siteHoarding: z.string().nullable().optional(),
  safetyArrangements: z.string().nullable().optional(),
  trainingRequirements: z.string().nullable().optional(),
  asbestosManagement: z.string().nullable().optional(),
  permitToWork: z.string().nullable().optional(),
  environmentalManagement: z.string().nullable().optional(),
  hazardAssessments: RamsHazardAssessmentsSchema.optional(),
  hazardEdits: RamsHazardEditsSchema.optional(),
  // RAMS-only (web validator asserts this subset against the real request).
  selectedWorkers: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        company: z.string().nullable().optional(),
        role: z.string(),
        phone: z.string().nullable().optional(),
        firstAidTrained: z.boolean(),
        fireWardenTrained: z.boolean().optional(),
      }),
    )
    .optional(),
  supervisorName: z.string().nullable().optional(),
}).strict();
export type CanonicalGenerationPayloadSubsetV1 = z.infer<typeof CanonicalGenerationPayloadSubsetV1Schema>;

/**
 * The project argument must come from the current, authoritative project
 * response. Draft answers cannot supply or overwrite project facts. This
 * function intentionally has no recovery/notes parameter or spread.
 */
export function buildCanonicalGenerationPayloadV1(
  draftInput: unknown,
  authoritativeProjectInput: unknown,
): CanonicalGenerationPayloadSubsetV1 {
  const draft = CanonicalGenerationDraftV1Schema.parse(draftInput);
  const confirmedProjectData = AuthoritativeProjectFactsV1Schema.parse(authoritativeProjectInput);
  const base = { confirmedProjectData, selectedHazardSlugs: [] as string[] };

  switch (draft.documentType) {
    case "COSHH":
      return CanonicalGenerationPayloadSubsetV1Schema.parse({
        ...base,
        preparedBy: draft.answers.preparedBy,
        approvedBy: draft.answers.approvedBy,
      });
    case "SITE_INDUCTION":
      return CanonicalGenerationPayloadSubsetV1Schema.parse({
        ...base, reportingProcedures: draft.answers.reportingProcedures,
      });
    case "EMERGENCY_PLAN":
      return CanonicalGenerationPayloadSubsetV1Schema.parse({
        ...base, emergencyContacts: draft.answers.emergencyContacts,
      });
    case "CPP": {
      const expectedValues: ProjectExpectedValues = {};
      const touched = draft.cppProjectFieldsTouched?.filter(field => {
        if (draft.projectExpectedValues === undefined) return true; // installed client
        // Full CPP fields map directly to guarded Project columns.
        const baseline = draft.projectExpectedValues[field];
        if (baseline === undefined) throw new Error("Missing project edit baseline");
        const value = draft.answers[field] ?? "";
        if (value === (baseline ?? "") || (!value.trim() && !(baseline ?? "").trim())) return false;
        expectedValues[field] = baseline;
        return true;
      });
      return CanonicalGenerationPayloadSubsetV1Schema.parse({
        ...base,
        ...draft.answers,
        ...(touched !== undefined ? { cppProjectFieldsTouched: touched } : {}),
        ...(draft.projectExpectedValues !== undefined ? { expectedValues } : {}),
      });
    }
    case "CPP_LIGHTWEIGHT":
      return CanonicalGenerationPayloadSubsetV1Schema.parse({
        ...base,
        lightweight: true,
        preparedBy: draft.answers.preparedBy,
        ...(draft.cppProjectFieldsTouched !== undefined
          ? { cppProjectFieldsTouched: draft.cppProjectFieldsTouched } : {}),
      });
    case "RAMS": {
      let supervisorName = draft.answers.supervisorName;
      const expectedValues: ProjectExpectedValues = {};
      if (draft.projectExpectedValues !== undefined && supervisorName?.trim()) {
        const baseline = draft.projectExpectedValues.worksSupervisorName;
        if (baseline === undefined) throw new Error("Missing supervisor edit baseline");
        if (supervisorName === (baseline ?? "")) supervisorName = null;
        else expectedValues.worksSupervisorName = baseline;
      }
      return CanonicalGenerationPayloadSubsetV1Schema.parse({
        ...base,
        selectedHazardSlugs: draft.answers.selectedHazardSlugs,
        ...(draft.answers.hazardAssessments !== undefined ? { hazardAssessments: draft.answers.hazardAssessments } : {}),
        ...(draft.answers.hazardEdits !== undefined ? { hazardEdits: draft.answers.hazardEdits } : {}),
        selectedWorkers: draft.answers.selectedWorkers,
        supervisorName,
        ...(draft.projectExpectedValues !== undefined ? { expectedValues } : {}),
      });
    }
  }
}
